import { useState, useEffect, useRef, useCallback } from "react";
import { getApiUrl } from "../config/api";

/**
 * Custom hook to manage RFQ streaming without WebSocket.
 * Uses REST fetch + polling for updates. All client actions are done via POST endpoints.
 */
export function useRFQStream(rfqId, clientId) {
  const [quotes, setQuotes] = useState([]);
  const [wsStatus, setWsStatus] = useState("idle"); // kept name for compatibility (was wsStatus)
  const [error, setError] = useState(null);
  const [rfqStatus, setRFQStatus] = useState("active");
  const pollingRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Cleanup previous polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (!rfqId) {
      setWsStatus("idle");
      setQuotes([]);
      return;
    }

    let aborted = false;
    const fetchQuotesOnce = async () => {
      setWsStatus("fetching");
      try {
        const url = getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}`);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const data = await res.json();
        // Expect server to return { quotes: [...] } or { ... , quotes } or an array
        const incoming =
          Array.isArray(data) ? data :
          Array.isArray(data.quotes) ? data.quotes :
          data?.quotes ?? [];
        if (!aborted && isMounted.current) {
          setQuotes(incoming);
          setWsStatus("connected");
          setRFQStatus(data.status || data.rfqStatus || "active");
          setError(null);
        }
      } catch (err) {
        if (!aborted && isMounted.current) {
          console.warn("useRFQStream fetch error:", err);
          setError(err.message || "Failed to load quotes");
          setWsStatus("error");
        }
      }
    };

    // Initial fetch immediately
    fetchQuotesOnce();

    // Poll every 5s for updates (adjustable)
    pollingRef.current = setInterval(fetchQuotesOnce, 5000);

    return () => {
      aborted = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [rfqId]);

  // Helper to update local quote record
  const updateLocalQuote = useCallback((predicate, patch) => {
    setQuotes((prev) => prev.map((q) => (predicate(q) ? { ...q, ...patch } : q)));
  }, []);

  const acceptQuote = useCallback(async (quoteId, dealerId) => {
    if (!rfqId) return;
    try {
      const url = clientId 
        ? getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}/accept?clientId=${encodeURIComponent(clientId)}`)
        : getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}/accept`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quoteId, dealerId, timestamp: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error(`Accept failed: ${res.status}`);
      // Best-effort update locally
      updateLocalQuote((q) => q.id === quoteId || q.id === quoteId, { status: "accepted" });
    } catch (err) {
      console.error("acceptQuote error:", err);
      setError(err.message || "Failed to accept quote");
    }
  }, [rfqId, clientId, updateLocalQuote]);

  const rejectQuote = useCallback(async (quoteId, dealerId) => {
    if (!rfqId) return;
    try {
      const url = clientId 
        ? getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}/reject?clientId=${encodeURIComponent(clientId)}`)
        : getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}/reject`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quoteId, dealerId, timestamp: new Date().toISOString(), reason: "Rejected by client" }),
      });
      if (!res.ok) throw new Error(`Reject failed: ${res.status}`);
      updateLocalQuote((q) => q.id === quoteId, { status: "rejected" });
    } catch (err) {
      console.error("rejectQuote error:", err);
      setError(err.message || "Failed to reject quote");
    }
  }, [rfqId, clientId, updateLocalQuote]);

  const sendCounterOffer = useCallback(async (dealerId, originalQuoteId, newPrice, notes = "") => {
    if (!rfqId) return;
    try {
      const url = clientId 
        ? getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}/counter?clientId=${encodeURIComponent(clientId)}`)
        : getApiUrl(`/api/rfq/${encodeURIComponent(rfqId)}/counter`);
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dealerId,
          originalQuoteId,
          price: newPrice,
          notes,
          timestamp: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(`Counter failed: ${res.status}`);
      // Optionally poll immediately to pick up dealer response later; perform a local optimistic update
      updateLocalQuote((q) => q.id === originalQuoteId, { status: "countered" });
    } catch (err) {
      console.error("sendCounterOffer error:", err);
      setError(err.message || "Failed to send counter offer");
    }
  }, [rfqId, clientId, updateLocalQuote]);

  return {
    quotes,
    wsStatus,
    error,
    rfqStatus,
    acceptQuote,
    rejectQuote,
    sendCounterOffer,
  };
}

/**
 * Hook to manage multiple RFQ streams (metadata only)
 * No WebSocket connections here either.
 */
export function useMultipleRFQStreams() {
  const [activeRFQs, setActiveRFQs] = useState(new Map());

  const addRFQ = useCallback((rfqId, metadata = {}) => {
    setActiveRFQs((prev) => {
      const next = new Map(prev);
      next.set(rfqId, {
        id: rfqId,
        startedAt: new Date().toISOString(),
        ...metadata,
      });
      return next;
    });
  }, []);

  const removeRFQ = useCallback((rfqId) => {
    setActiveRFQs((prev) => {
      const next = new Map(prev);
      next.delete(rfqId);
      return next;
    });
  }, []);

  return {
    activeRFQs: Array.from(activeRFQs.values()),
    addRFQ,
    removeRFQ,
  };
}
